import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { AppLink } from '~/components/AppLink';

import { Calendar, Clock, CheckCircle, CircleDot } from 'lucide-react';

interface Props {
  readonly title: string;
  readonly upcoming: any[];
  readonly booked: any[];
  readonly link: string;
  readonly hideViewAllButton?: boolean;
}

export function CockpitCalendarEventWidget(props: Props) {
  const { t } = useTranslate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingBooked = (props.booked || []).filter(
    (e) => new Date(e.date) >= today,
  ).length;
  const upcomingFree = (props.upcoming || []).length - upcomingBooked;
  const totalBooked = (props.booked || []).length;
  const totalUpcoming = (props.upcoming || []).length;

  const metrics = [
    {
      label:
        t('cockpit_calendar_upcoming_appointments') || 'Anstehende Termine',
      value: totalUpcoming,
      icon: Calendar,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      label: t('cockpit_calendar_booked_upcoming') || 'Gebuchte Termine',
      value: upcomingBooked,
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      label: t('cockpit_calendar_available_upcoming') || 'Verfügbare Termine',
      value: upcomingFree,
      icon: CircleDot,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    },
    {
      label: t('cockpit_calendar_total_booked') || 'Insgesamt gebucht',
      value: totalBooked,
      icon: Clock,
      color: 'text-primary-600 dark:text-primary-400',
      bgColor: 'bg-primary-100 dark:bg-primary-900/30',
    },
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t(props.title)}
          </CardTitle>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Calendar className="h-4 w-4 text-primary" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        <div className="grid grid-cols-2 gap-4 flex-1">
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={index}
                className="group hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800/50 p-3 rounded-lg transition-colors duration-200 -mx-3"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-10 w-10 rounded-lg flex items-center justify-center ${metric.bgColor}`}
                  >
                    <IconComponent className={`h-4 w-4 ${metric.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
                      {metric.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {metric.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!props.hideViewAllButton && (
          <AppLink
            to={props.link}
            className="mt-4"
          >
            <Button
              primary
              showArrow
              className="w-full"
            >
              {t('cockpit_calendar_see_all') || 'Alle anzeigen'}
            </Button>
          </AppLink>
        )}
      </CardContent>
    </Card>
  );
}
